import React, { useState } from 'react';

import { StructuredTaskPanel } from '@/common/components/StructuredTaskDetails';
import { TaskHeader } from '@/common/components/TaskHeader';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Typography } from '@mui/material';

import { Loading } from '../../common/components/Loading';
import { Resource } from '../../common/constants/resource';
import { RenderAddressSection } from '../detail/tasks/trl/TRLAddressSection';
import { RenderApplicantSection } from '../detail/tasks/trl/TRLApplicantSection';
import { RenderCreditSection } from '../detail/tasks/trl/TRLCreditSection';
import { RenderEmploymentSection } from '../detail/tasks/trl/TRLEmplolymentSection';
import { RenderScoresSection } from '../detail/tasks/trl/TRLScoresSection';
import { valueToString } from '../detail/utilities/formattingUtilities';
import { factsExist } from '../detail/utilities/taskDetailUtilities';
import { Evidence } from '../tasks/types/evidence';
import { TaskTypes } from '../tasks/types/taskTypes';
import { TRL } from '../tasks/types/trl';
import { fetchEvidenceData } from './helpers';

export const TRLDetail = ({
  product,
  carrier,
  policyId,
  evidenceId,
}: {
  product: string;
  carrier: string;
  policyId: string;
  evidenceId: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [evidenceInprogress, setEvidenceInprogress] = useState(false);
  const [evidenceReady, setEvidenceReady] = useState(false);
  const doExpand = async () => {
    if (!expanded) {
      if (!evidenceReady && !evidenceInprogress) {
        // fetch ruleResultData
        setEvidenceInprogress(true);
        const result = await fetchEvidenceData(
          product,
          carrier,
          policyId,
          evidenceId.toString(),
          TaskTypes.TRL,
        );
        setEvidence(result);
        setEvidenceReady(true);
        setEvidenceInprogress(false);
      }
    }
    setExpanded(!expanded);
  };

  function renderTaskDetails() {
    const trl: TRL | null = evidence ? (evidence.facts as TRL) : null;
    if (!trl) {
      return <></>;
    }
    return (
      <StructuredTaskPanel>
        <TaskHeader
          title={Resource.TASK_TITLE_TRL}
          hitTypeLabel={formatHitType(trl.applicant.hitType)}
        />
        {trl && !trl.recordExists && !evidence?.errorMessage && (
          <p>{Resource.TRL_NO_RECORD}</p>
        )}
        {trl && trl.recordExists && (
          <>
            <RenderScoresSection
              score={trl.trlScore}
              onFileSince={trl.credit.onFileSince}
            />
            <RenderApplicantSection applicant={trl.applicant} />
            <RenderCreditSection credit={trl.credit} />
            {trl.addressHistory.length > 0 && (
              <RenderAddressSection addressHistory={trl.addressHistory} />
            )}
            {trl.employmentHistory.length > 0 && (
              <RenderEmploymentSection
                employmentHistory={trl.employmentHistory}
              />
            )}
          </>
        )}
      </StructuredTaskPanel>
    );
  }

  return (
    <Box pt={8}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={-0.15} ml={-3.5} position="absolute">
            <IconButton
              style={{ width: '24px' }}
              size="small"
              onClick={doExpand}
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={expanded ? 90 : undefined}
              />
            </IconButton>
          </Box>
          <Typography variant="h2">TRL</Typography>
        </Box>
      </Box>

      {expanded &&
        (evidenceInprogress ? (
          <Loading fullscreen={false} />
        ) : (
          evidenceReady &&
          (evidence ? (
            factsExist(evidence) ? (
              renderTaskDetails()
            ) : (
              <></>
            )
          ) : (
            <Box>
              <Typography variant="body1">failed to load trl data</Typography>
            </Box>
          ))
        ))}
    </Box>
  );
};

function formatHitType(hitType: string) {
  switch (hitType) {
    case Resource.TRL_APPLICANT_HIT_TYPE_NO_HIT_KEY:
      return Resource.TRL_APPLICANT_HIT_TYPE_NO_HIT_VALUE;
    case Resource.TRL_APPLICANT_HIT_TYPE_REGULAR_HIT_KEY:
      return Resource.TRL_APPLICANT_HIT_TYPE_REGULAR_HIT_VALUE;
    default:
      return valueToString(hitType);
  }
}
